import { useState, useRef, useEffect } from 'react';
import { CrudPage } from '../../components/CrudPage';
import { type RoutingDto } from '../../api/models/RoutingDto';
import { type MaterialDto } from '../../api/models/MaterialDto';
import RoutingFormDialog from '../forms/RoutingFormDialog.tsx';
import { RoutingControllerService } from '../../api/services/RoutingControllerService';
import { MaterialControllerService } from '../../api/services/MaterialControllerService';
import { useTranslation } from 'react-i18next';
import RoutingVisualizationDialog from '../../components/RoutingVisualizationDialog';
import RoutingImportDialog from '../../components/RoutingImportDialog';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import { useToast } from '../../hooks/useToast';
import { useStore } from '../../../store';
import { hasPermission } from '../../utils/permissions';
import { getRoutingWeekdayVariantsFlag } from '../../utils/featureFlags';
import { persistRouting, type RoutingWithId } from './routingPersistence';

// Type with required id for the CrudPage

type RoutingType = 'PRODUCT_ROUTING' | 'INTERMEDIATE_ROUTING';
type MaterialType = 'PRODUCT' | 'INTERMEDIATE_PRODUCT';

type RoutingsPageProps = {
  routingType?: RoutingType;
  materialType?: MaterialType;
  titleKey?: string;
  entityName?: string;
};

const RoutingsPage = ({
  routingType = 'PRODUCT_ROUTING',
  materialType = 'PRODUCT',
  titleKey = 'headers.routings',
  entityName = 'routing',
}: RoutingsPageProps) => {
  const { t } = useTranslation();
  const { handleError } = useApiErrorHandler();
  const { showToast } = useToast();
  const userProfile = useStore((state) => state.userProfile);
  const [visualizationDialogOpen, setVisualizationDialogOpen] = useState(false);
  const [selectedRoutingForVisualization, setSelectedRoutingForVisualization] = useState<RoutingDto | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  // Weekday variants: feature flag gates the 'set as default' toolbar action
  // (weekday selection itself lives in RoutingFormDialog as part of the routing record).
  const [variantsFeatureEnabled, setVariantsFeatureEnabled] = useState<boolean>(false);

  useEffect(() => {
    getRoutingWeekdayVariantsFlag()
      .then((res) => setVariantsFeatureEnabled(Boolean(res?.enabled)))
      .catch(() => setVariantsFeatureEnabled(false));
  }, []);
  
  // Check permissions
  const canImport = hasPermission('routing:import', userProfile);
  const canExport = hasPermission('routing:export', userProfile);
  
  // Store routings in a ref to access them in deleteData
  const currentRoutingsRef = useRef<RoutingWithId[]>([]);
  // Store material map for renderer access (persistent across renders)
  const materialMapRef = useRef<Map<string, MaterialDto>>(new Map());

  // Helper function to translate and format completeness issues
  const formatCompletenessInfo = (completenessInfo: string): string => {
    if (!completenessInfo) {
      return t('routing.status.incomplete', 'Unvollständig - benötigt weitere Konfiguration');
    }

    // Split by "; " to get individual issues
    const issues = completenessInfo.split('; ').filter(issue => issue.trim());
    
    if (issues.length === 0) {
      return t('routing.status.incomplete', 'Unvollständig - benötigt weitere Konfiguration');
    }

    // Translate each issue
    const translatedIssues = issues.map(issue => {
      const trimmed = issue.trim();
      
      // Pattern matching for known German messages
      if (trimmed === 'Routing hat keine Operations definiert') {
        return t('routing.status.issue.noOperations', 'Arbeitsplan hat keine Vorgänge definiert');
      }
      if (trimmed.startsWith("Operation '") && trimmed.includes("' hat keine Operation Modes definiert")) {
        const operationName = trimmed.match(/Operation '([^']+)'/)?.[1] || '';
        return t('routing.status.issue.noOperationModes', 'Vorgang "{{name}}" hat keine Ausführungsmodi definiert', { name: operationName });
      }
      if (trimmed.startsWith("Operation '") && trimmed.includes("' hat einen Mode ohne Resource")) {
        const operationName = trimmed.match(/Operation '([^']+)'/)?.[1] || '';
        return t('routing.status.issue.modeWithoutResource', 'Vorgang "{{name}}" hat einen Ausführungsmodus ohne Ressource', { name: operationName });
      }
      if (trimmed.startsWith("Operation '") && trimmed.includes("' Mode für Resource '") && trimmed.includes("' hat keine gültige Duration")) {
        const match = trimmed.match(/Operation '([^']+)' Mode für Resource '([^']+)' hat keine gültige Duration/);
        if (match) {
          const operationName = match[1];
          const resourceName = match[2];
          return t('routing.status.issue.invalidDuration', 'Vorgang "{{operationName}}" hat für Ressource "{{resourceName}}" keine gültige Dauer', { operationName, resourceName });
        }
      }
      if (trimmed.startsWith("Operation '") && trimmed.includes("' hat keinen TimeType definiert")) {
        const operationName = trimmed.match(/Operation '([^']+)'/)?.[1] || '';
        return t('routing.status.issue.noTimeType', 'Vorgang "{{name}}" hat keinen Zeittyp definiert', { name: operationName });
      }
      if (trimmed === 'Routing hat kein Material zugeordnet') {
        return t('routing.status.issue.noMaterial', 'Arbeitsplan hat kein Material zugeordnet');
      }
      if (trimmed === 'Routing hat kein Output Material definiert') {
        return t('routing.status.issue.noOutputMaterial', 'Arbeitsplan hat kein Ausgabematerial definiert');
      }
      if (trimmed === 'Routing hat keine Dough Quantity Range definiert (minDoughQuantityKg/maxDoughQuantityKg)') {
        return t('routing.status.issue.noDoughQuantityRange', 'Arbeitsplan hat keine Teigmenge-Bereich definiert');
      }
      
      // Fallback: return original if no pattern matches
      return trimmed;
    });

    // Format as structured list (bullet points)
    if (translatedIssues.length === 1) {
      return translatedIssues[0];
    } else {
      return translatedIssues.map(issue => `• ${issue}`).join('\n');
    }
  };

  // Custom renderer for routing status with icon and tooltip
  const statusRenderer = (props: any) => {
    // Syncfusion Grid passes data in different ways - try all possibilities
    const routing = props?.data || props?.rowData || props;
    const status = routing?.status || 'INCOMPLETE';
    const completenessInfo = routing?.completenessInfo || '';
    
    const isComplete = status === 'COMPLETE';
    const iconColor = isComplete ? '#22c55e' : '#f59e0b'; // green-500 : amber-500
    const iconSymbol = isComplete ? '✓' : '⚠'; // Checkmark or Warning
    const tooltipText = isComplete 
      ? t('routing.status.complete', 'Vollständig - kann verwendet werden')
      : formatCompletenessInfo(completenessInfo);
    
    return (
      <div 
        className="flex items-center justify-center"
        title={tooltipText}
        style={{ cursor: 'help' }}
      >
        <span 
          style={{ 
            color: iconColor, 
            fontSize: '20px',
            fontWeight: 'bold',
            lineHeight: '1'
          }}
        >
          {iconSymbol}
        </span>
      </div>
    );
  };

  // Custom renderer for product/material name
  const productRenderer = (props: any) => {
    // Syncfusion Grid passes data in different ways - try all possibilities
    const routing = props?.data || props?.rowData || props;
    if (!routing) {
      return '-';
    }
    
    const materialId = routing.materialId;
    
    if (!materialId) {
      return '-';
    }
    
    // Find material from the map created in fetchData (ref variable)
    const material = materialMapRef.current.get(materialId);
    
    if (!material) {
      // Material not found in map - might be a timing issue
      // Return materialId as fallback for debugging
      return materialId.substring(0, 8) + '...';
    }
    
    return material.name || '-';
  };


  // Custom renderer for name with description tooltip
  const nameRenderer = (props: any) => {
    // Syncfusion Grid passes data in different ways - try all possibilities
    const routing = props?.data || props?.rowData || props;
    const name = routing?.name || '';
    const description = routing?.description || '';
    
    if (!name) {
      return '';
    }
    
    if (!description) {
      return name;
    }
    
    return (
      <div 
        title={description}
        style={{ cursor: 'help' }}
      >
        {name}
      </div>
    );
  };

  // Custom renderer for operation relationships count (only number)
  const operationRelationshipsRenderer = (props: any) => {
    // Syncfusion Grid passes data in different ways - try all possibilities
    const routing = props?.data || props?.rowData || props;
    const relationships = routing?.operationRelationships || [];
    const count = relationships.length;
    return count.toString();
  };

  // Convert a weekday_mask byte into a human-readable label ("Standard", "Sa+So", "Feiertag", ...).
  // weekdayMask is typed string by the OpenAPI codegen (Java Byte) but arrives as number at runtime
  // for routings fetched from the backend; Number() handles both.
  const formatWeekdayMask = (rawMask: unknown): string => {
    const mask = typeof rawMask === 'number' ? rawMask : Number(rawMask ?? 0);
    if (!mask) {
      return t('routing.variants.defaultLabel', 'Standard');
    }
    const keys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday', 'holiday'] as const;
    const parts: string[] = [];
    for (let i = 0; i < 8; i++) {
      if ((mask & (1 << i)) !== 0) {
        parts.push(t(`routing.variants.weekdayShort.${keys[i]}`, keys[i]));
      }
    }
    return parts.join('+');
  };

  const weekdayMaskRenderer = (props: any) => {
    const routing = props?.data || props?.rowData || props;
    const mask = typeof routing?.weekdayMask === 'number'
      ? routing.weekdayMask
      : Number(routing?.weekdayMask ?? 0);
    const isDefault = !mask;
    return (
      <span
        className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
          isDefault ? 'bg-gray-200 text-gray-700' : 'bg-blue-100 text-blue-800'
        }`}
        title={isDefault
          ? t('routing.variants.defaultHint', 'Greift für alle Tage ohne explizite Variante.')
          : t('routing.variants.description',
              'Tagesabhängige Arbeitsplan-Varianten — der Resolver wählt bei der PO-Erzeugung anhand des Planungstags.')}
      >
        {formatWeekdayMask(routing?.weekdayMask)}
      </span>
    );
  };

  // Handle import button click
  const handleImport = () => {
    setImportDialogOpen(true);
  };

  // Handle import success
  const handleImportSuccess = () => {
    // The CrudPage will automatically refresh when the dialog closes
    // We can trigger a manual refresh if needed
    window.location.reload(); // Simple approach - could be improved with state management
  };

  // Handle export button click
  const handleExport = (selectedItems: RoutingWithId[]) => {
    if (selectedItems.length !== 1) {
      showToast('warning', t('common.warning'), t('routing.export.noSelection', 'Bitte wählen Sie genau ein Routing zum Exportieren aus.'));
      return;
    }

    try {
      // Convert selected item to RoutingDto format (remove id field)
      const { id, ...routingToExport } = selectedItems[0];
      const routing = routingToExport as RoutingDto;

      // Create JSON blob
      const jsonString = JSON.stringify(routing, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);

      // Create download link
      const link = document.createElement('a');
      link.href = url;
      
      // Use routing name for filename
      const fileName = routing.name
        ? `${routing.name.replace(/[^a-z0-9]/gi, '_')}.json`
        : `routing_export_${new Date().toISOString().split('T')[0]}.json`;
      
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showToast('success', t('common.success'), t('routing.export.success', 'Routing erfolgreich exportiert'));
    } catch (error) {
      handleError(error, 'exporting routing');
      showToast('error', t('common.error'), t('routing.export.error', 'Fehler beim Exportieren des Routings'));
    }
  };

  // Handle visualization button click
  const handleVisualize = (selectedItems: RoutingWithId[]) => {
    if (selectedItems.length === 1) {
      const routing = selectedItems[0];
      // Remove the id field (it's only for CrudPage compatibility)
      const { id, ...routingWithoutId } = routing;
      setSelectedRoutingForVisualization(routingWithoutId as RoutingDto);
      setVisualizationDialogOpen(true);
    }
  };

  // === Weekday variants action handler ===

  // weekdayMask comes from the codegen as number (after the Byte→Integer change on the backend);
  // Number() is still defensive against any string form e.g. from a stale cache.
  const maskOf = (r: RoutingWithId): number =>
    typeof r.weekdayMask === 'number' ? r.weekdayMask : Number(r.weekdayMask ?? 0);

  const handlePromoteVariant = async (selectedItems: RoutingWithId[]) => {
    if (selectedItems.length !== 1 || !selectedItems[0].uuid) return;
    const ok = window.confirm(
      `${t('routing.variants.promote.confirmTitle', 'Variante zur Standard-Variante befördern?')}\n\n` +
        t(
          'routing.variants.promote.confirmMessage',
          'Die aktuelle Standard-Variante übernimmt im Tausch die Wochentage dieser Variante. Bestätigen?',
        ),
    );
    if (!ok) return;
    try {
      await RoutingControllerService.promoteVariantToDefault(selectedItems[0].uuid);
      showToast(
        'success',
        t('common.success', 'Erfolg'),
        t('routing.variants.promote.success', 'Variante ist jetzt Standard'),
      );
      window.location.reload();
    } catch (error) {
      handleError(error, 'promoting variant');
    }
  };

  return (
    <>
      <CrudPage<RoutingWithId, number>
        titleKey={titleKey}
        getCustomToolbarButtons={(selectedItems) => [
          ...(canExport ? [{
            text: t('routing.export.button', 'Exportieren'),
            tooltipText: t('routing.export.tooltip', 'Ausgewähltes Routing als JSON exportieren'),
            prefixIcon: 'e-icons e-download',
            id: 'export',
            disabled: selectedItems.length !== 1,
            onClick: handleExport,
          }] : []),
          ...(canImport ? [{
            text: t('routing.import.button', 'Importieren'),
            tooltipText: t('routing.import.tooltip', 'Routing aus JSON-Datei importieren'),
            prefixIcon: 'e-icons e-upload',
            id: 'import',
            disabled: false,
            onClick: handleImport,
          }] : []),
          {
            text: t('routing.visualization.button', 'Visualisieren'),
            tooltipText: t('routing.visualization.tooltip', 'Arbeitsplan als PERT-Diagramm visualisieren'),
            prefixIcon: 'e-icons e-diagram',
            id: 'visualize',
            disabled: selectedItems.length !== 1,
            onClick: handleVisualize,
          },
          ...(variantsFeatureEnabled ? [
            {
              text: t('routing.variants.promote.button', 'Als Standard festlegen'),
              tooltipText: t('routing.variants.promote.tooltip',
                'Diese Variante zum Standard-Arbeitsplan machen (Mask-Swap mit aktuellem Standard)'),
              prefixIcon: 'e-icons e-check',
              id: 'promote-variant',
              disabled: selectedItems.length !== 1 || maskOf(selectedItems[0]) === 0,
              onClick: handlePromoteVariant,
            },
          ] : []),
        ]}
          gridMeta={[
      { 
        field: 'status', 
        headerKey: 'routing.grid.field.status', 
        width: 80, 
        align: 'Center',
        render: statusRenderer
      },
      { 
        field: 'name', 
        headerKey: 'routing.grid.field.name', 
        width: 200,
        render: nameRenderer
      },
      { 
        field: 'materialId', 
        headerKey: 'routing.grid.field.product', 
        width: 200, 
        align: 'Left',
        render: productRenderer
      },
      {
        field: 'weekdayMask',
        headerKey: 'routing.variants.title',
        width: 130,
        align: 'Center',
        render: weekdayMaskRenderer
      },
      { field: 'operations.length', headerKey: 'routing.grid.field.operationCount', width: 120, align: 'Center' },
      { field: 'operationRelationships', headerKey: 'routing.grid.field.operationRelationships', width: 200, align: 'Center', render: operationRelationshipsRenderer },
      { field: 'resourceRequirements.length', headerKey: 'routing.grid.field.resourceRequirementCount', width: 150, align: 'Center' }
    ]}
      fetchData={async () => {
        
        // Test if the tenant header function is working
        let routings;
        try {
          routings = await RoutingControllerService.findAll();
        } catch (error: unknown) {
          console.error('❌ RoutingsPage fetchData - Routing API error:', error);
          console.error('❌ RoutingsPage fetchData - Error details:', {
            message: (error as any)?.message,
            status: (error as any)?.status,
            response: (error as any)?.response,
            body: (error as any)?.body
          });
          throw error;
        }
        
        let materials;
        try {
          materials = await MaterialControllerService.findAll();
        } catch (error: unknown) {
          console.error('❌ RoutingsPage fetchData - Material API error:', error);
          console.error('❌ RoutingsPage fetchData - Error details:', {
            message: (error as any)?.message,
            status: (error as any)?.status,
            response: (error as any)?.response,
            body: (error as any)?.body
          });
          throw error;
        }
        
        // Filter materials to match routing type context
        const filteredMaterials = materials.filter((material: any) => 
          material.productionType === 'MAKE' && material.materialType === materialType
        );

        // Create a map of materials by ID for quick lookup
        materialMapRef.current = new Map<string, MaterialDto>();
        filteredMaterials.forEach((material: any) => {
          if (material.uuid) {
            materialMapRef.current.set(material.uuid, material);
          }
        });
        
        
        // Filter by routing type context
        const filteredRoutings = routings.filter((routing: any) => routing.routingType === routingType);

        // Map routings with IDs
        const routingsWithSummary = filteredRoutings.map((routing: any, index: number) => {
          return {
            ...routing,
            id: index + 1 // Use index as temporary ID for the grid
          } as RoutingWithId;
        });
        
        // Store the current routings for deleteData to access
        currentRoutingsRef.current = routingsWithSummary;
        return routingsWithSummary;
      }}
      saveData={async (data: RoutingWithId) => {
        // Remove temporary UUIDs from operations so backend generates new ones
        // Create a map of temp UUIDs to operation indices for mapping relationships
        const tempUuidToIndexMap = new Map<string, number>();
        const operationsToSave = data.operations?.map((op, index) => {
          if (op.uuid && op.uuid.startsWith('temp_')) {
            tempUuidToIndexMap.set(op.uuid, index);
            const { uuid, ...opWithoutUuid } = op;
            return opWithoutUuid;
          }
          return op;
        }) || [];
        
        // Map temporary UUIDs in relationships to operation indices
        // The backend will map these indices to the saved operations
        const operationRelationshipsToSave = data.operationRelationships?.map((rel) => {
          let predecessorId = rel.predecessorId;
          let successorId = rel.successorId;
          
          // Map temp UUIDs to indices
          if (predecessorId && predecessorId.startsWith('temp_')) {
            const index = tempUuidToIndexMap.get(predecessorId);
            if (index !== undefined) {
              predecessorId = String(index);
            }
          }
          
          if (successorId && successorId.startsWith('temp_')) {
            const index = tempUuidToIndexMap.get(successorId);
            if (index !== undefined) {
              successorId = String(index);
            }
          }
          
          const relWithoutUuid = {
            ...rel,
            predecessorId,
            successorId
          };
          
          if (rel.uuid && rel.uuid.startsWith('temp_')) {
            const { uuid, ...relFinal } = relWithoutUuid;
            return relFinal;
          }
          return relWithoutUuid;
        }) || [];
        
        const dataToSave = {
          ...data,
          operations: operationsToSave,
          operationRelationships: operationRelationshipsToSave,
          // Map temporary UUIDs in resourceRequirements to operation indices (same as operationRelationships)
          resourceRequirements: data.resourceRequirements?.map((rr) => {
            let fromOperationId = rr.fromOperationId;
            let toOperationId = rr.toOperationId;
            let conditionalOperationId = rr.conditionalOperationId;
            
            // Map temp UUIDs to indices for fromOperationId
            if (fromOperationId && fromOperationId.startsWith('temp_')) {
              const index = tempUuidToIndexMap.get(fromOperationId);
              if (index !== undefined) {
                fromOperationId = String(index);
              }
            }
            
            // Map temp UUIDs to indices for toOperationId
            if (toOperationId && toOperationId.startsWith('temp_')) {
              const index = tempUuidToIndexMap.get(toOperationId);
              if (index !== undefined) {
                toOperationId = String(index);
              }
            }
            
            // Map temp UUIDs to indices for conditionalOperationId (if present)
            if (conditionalOperationId && conditionalOperationId.startsWith('temp_')) {
              const index = tempUuidToIndexMap.get(conditionalOperationId);
              if (index !== undefined) {
                conditionalOperationId = String(index);
              }
            }
            
            const rrWithMappedIds = {
              ...rr,
              fromOperationId,
              toOperationId,
              ...(conditionalOperationId !== undefined && { conditionalOperationId })
            };
            
            // Remove temporary UUID from resource requirement itself
            if (rr.uuid && rr.uuid.startsWith('temp_')) {
              const { uuid, ...rrFinal } = rrWithMappedIds;
              return rrFinal;
            }
            return rrWithMappedIds;
          })
        };
        
        
        // Backend's saveRoutingWithSubEntities handles operations, relationships and requirements.
        // Every error, a 400 included, propagates: CrudPage shows it and keeps the dialog open.
        return persistRouting(dataToSave, data.id || 0);
      }}
      deleteData={async (id: number) => {
        // Find the routing by the temporary ID and use its UUID for deletion
        const routing = currentRoutingsRef.current.find((r: RoutingWithId) => r.id === id);
        if (routing?.uuid) {
          await RoutingControllerService.delete(routing.uuid);
        }
      }}
      FormComponent={RoutingFormDialog}
      entityName={entityName}
      showImportButton={false}
      formProps={{ routingType, materialType }}
      />
      
      <RoutingVisualizationDialog
        routing={selectedRoutingForVisualization}
        visible={visualizationDialogOpen}
        onClose={() => {
          setVisualizationDialogOpen(false);
          setSelectedRoutingForVisualization(null);
        }}
      />
      
      <RoutingImportDialog
        isOpen={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImportSuccess={handleImportSuccess}
      />
    </>
  );
};

export default RoutingsPage; 